import { useState, useMemo } from "react";
import {
  TitleRegular,
  TitleMedium,
  TitleSmall,
  BodySecondary,
  BodyCaption,
  COLORS,
  Button,
  Switch,
  StyledModal,
} from "@bikdotai/bik-component-library";
import styled from "styled-components";
import { QRCodeSVG } from "qrcode.react";
import { useAppSelector } from "../../state/store";
import ChevronRight from "../../icons/chevron_right";
import AlertIcon from "../../icons/alertIcon";
import { dasboardUrl } from "../../config";
import { generateTriggerCode, formatTriggerCode } from "../../utilities/triggerCodeGenerator";
import { CHANNELS } from "../../utilities/login.utility";

const getPreviewModeAlertText = (channel: string): string => {
  switch (channel) {
    case CHANNELS.WHATSAPP:
      return "No WhatsApp numbers added in test mode settings to test.";
    case CHANNELS.INSTAGRAM:
      return "No Instagram account added in test mode settings to test.";
    case CHANNELS.EMAIL:
      return "No Email added in test mode settings to test.";
    default:
      return "";
  }
};

interface ITestModeModalContent {
  flowId: string;
  storeId: string;
  channel: string;
  isTestMode: boolean;
  showTestModeUserAlert: boolean;
  onTestModeToggle: (value: boolean) => void;
  onClose: () => void;
}

export const TestModeModalContent = ({
  flowId,
  storeId,
  channel,
  isTestMode,
  showTestModeUserAlert,
  onTestModeToggle,
  onClose,
}: ITestModeModalContent) => {
  const [showLiveTestModal, setShowLiveTestModal] = useState(false);
  const [isToggleLoading, setIsToggleLoading] = useState(false);
  const flowMetaState = useAppSelector((state) => state.flowMetaState.flowMeta);
  const channels = useAppSelector((state) => state.flowMetaState.channels);

  const triggerType = (flowMetaState as any)?.[flowId]?.triggerType;
  const flowTriggers = (flowMetaState as any)?.[flowId]?.triggers ?? [];

  // Show Live test for:
  // 1. WhatsApp/Instagram: keyword trigger type
  // 2. Email: event trigger type with emailReceived header
  // 3. Never for livechat
  const isKeywordTrigger = triggerType === "keyword";
  const isEmailReceivedTrigger =
    channel === CHANNELS.EMAIL &&
    triggerType === "event" &&
    flowTriggers.some((t: any) => t.header === "emailReceived");
  const isLiveTestEligible =
    channel !== CHANNELS.LIVECHAT && (isKeywordTrigger || isEmailReceivedTrigger);

  const previewModeAlertText = useMemo(() => getPreviewModeAlertText(channel), [channel]);

  // Generate trigger code based on flowId (matches backend FlowCodeUtils)
  const triggerCode = useMemo(() => {
    return generateTriggerCode(flowId);
  }, [flowId]);

  // Get phone number and trigger code from flow-keywords
  const getWhatsAppLink = () => {
    const flowKeyword = (flowMetaState as any)?.[flowId];

    // For keyword flows, triggers array is empty.
    // The configured channels are stored as keys in keywordsNew: { [phoneNumberId]: { ... } }
    // Take the first phoneNumberId from keywordsNew and look up its accountNumber
    let phoneNumber = "";
    const keywordsNew = flowKeyword?.keywordsNew;
    if (keywordsNew && Object.keys(keywordsNew).length > 0) {
      const firstPhoneNumberId = Object.keys(keywordsNew)[0];
      const matchedChannel = channels?.multiWhatsappInfo?.find(
        (info: any) => info.phoneNumberId === firstPhoneNumberId
      );
      if (matchedChannel) {
        phoneNumber = matchedChannel.accountNumber.replace(/\+/g, "").replace(/\s/g, "");
      }
    }

    // Include # in the trigger code for WhatsApp
    const codeWithHash = formatTriggerCode(triggerCode);
    const encodedText = encodeURIComponent(codeWithHash);
    return `https://api.whatsapp.com/send?phone=${phoneNumber}&text=${encodedText}`;
  };

  const getEmailLink = () => {
    const flowKeyword = (flowMetaState as any)?.[flowId];
    
    // Get email address from emailReceived trigger's channelId
    let emailAddress = "";
    if (flowKeyword?.triggers && flowKeyword.triggers.length > 0) {
      const emailTrigger = flowKeyword.triggers.find(
        (trigger: any) => trigger.header === "emailReceived" && trigger.channelId
      );
      emailAddress = emailTrigger?.channelId ?? "";
    }

    // Fallback: use first configured email from channels
    if (!emailAddress && channels?.multiChannelEmail && channels.multiChannelEmail.length > 0) {
      emailAddress = channels.multiChannelEmail[0];
    }
    
    const codeWithHash = formatTriggerCode(triggerCode);
    const emailSubject = encodeURIComponent(codeWithHash);
    const emailBody = encodeURIComponent(codeWithHash);
    return `mailto:${emailAddress}?subject=${emailSubject}&body=${emailBody}`;
  };

  const whatsappLink = getWhatsAppLink();
  const emailLink = getEmailLink();
  const testLink = channel === CHANNELS.EMAIL ? emailLink : whatsappLink;

  const handleCopyText = () => {
    navigator.clipboard.writeText(formatTriggerCode(triggerCode));
  };

  const handleOpenLink = () => {
    if (channel === CHANNELS.EMAIL) {
      window.location.href = emailLink;
    } else {
      window.open(whatsappLink, "_blank");
    }
  };

  return (
    <>
      {/* Popover menu with two options */}
      <PopoverContainer>
        <PreviewModeCard>
          <PreviewModeCardHeader>
            <BodySecondary style={{ fontWeight: 400, color: COLORS.content.primary }}>
              Preview in test mode
            </BodySecondary>
            <Switch
              value={isTestMode}
              type="brand"
              isLoading={isToggleLoading}
              onValueChange={async (value: boolean) => {
                setIsToggleLoading(true);
                await onTestModeToggle(value);
                setIsToggleLoading(false);
              }}
            />
          </PreviewModeCardHeader>
          <BodyCaption style={{ color: COLORS.content.secondary, marginTop: "4px" }}>
            Test mode will run the journey for the customers added in the test mode settings on Journey dashboard
          </BodyCaption>
          {showTestModeUserAlert && channel !== CHANNELS.LIVECHAT && previewModeAlertText && (
            <PreviewWarningContainer>
              <AlertIcon width={20} height={20} color={COLORS.content.warning} />
              <BodyCaption>
                {previewModeAlertText}{" "}
                <a
                  href={`${dasboardUrl}/journeys/settings/test-mode`}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: COLORS.content.brand }}
                >
                  Go to test settings
                </a>
              </BodyCaption>
            </PreviewWarningContainer>
          )}
        </PreviewModeCard>

        {isLiveTestEligible && (
          <OptionCard onClick={() => {
            setShowLiveTestModal(true);
            onClose();
          }}>
            <OptionHeader>
              <BodySecondary style={{ fontWeight: 400, color: COLORS.content.primary }}>
                Live test
              </BodySecondary>
              <ChevronRight color={COLORS.content.secondary} />
            </OptionHeader>
            <BodyCaption style={{ color: COLORS.content.secondary, marginTop: "4px" }}>
              Test your journey in real time by scanning the QR code, clicking the link, or copying the message text
            </BodyCaption>
          </OptionCard>
        )}
      </PopoverContainer>

      {/* Live Test Mode Modal - Combined view */}
      {showLiveTestModal && (
        <StyledModal
          open={true}
          onClose={() => setShowLiveTestModal(false)}
          centralContainerStyles={{ width: channel === "instagram" ? "400px" : "460px", padding: "24px" }}
          zIndex={10000}
        >
          <LiveTestModalContent>
            {/* QR Code and Link Card - Only show for WhatsApp and Email */}
            {channel !== CHANNELS.INSTAGRAM && (
              <TestCard>
                <TitleMedium style={{ color: COLORS.content.primary, textAlign: "center", marginBottom: "20px" }}>
                  Test your journey live
                </TitleMedium>
                
                <SectionContainer>
                  <QRCodeContainer>
                    <QRCodeSVG value={testLink} size={180} />
                  </QRCodeContainer>
                  <BodySecondary style={{ color: COLORS.content.secondary, textAlign: "center", marginTop: "16px" }}>
                    Scan to test the journey instantly
                  </BodySecondary>
                </SectionContainer>

                <Divider insideCard>
                  <DividerText insideCard>or</DividerText>
                </Divider>

                <SectionContainer>
                  <BodySecondary style={{ color: COLORS.content.primary }}>
                    Click on the link to{" "}
                    <LinkButton onClick={handleOpenLink}>
                      run a live journey test
                    </LinkButton>
                  </BodySecondary>
                </SectionContainer>
              </TestCard>
            )}

            {channel !== CHANNELS.INSTAGRAM && (
              <Divider>
                <DividerText>or</DividerText>
              </Divider>
            )}

            {/* Manual Code Card */}
            {channel === CHANNELS.INSTAGRAM && (
              <TitleMedium style={{ color: COLORS.content.primary, textAlign: "center", marginBottom: "20px" }}>
                Test your journey live
              </TitleMedium>
            )}
            <TestCard>
              <CodeDisplayContainer>
                <TitleRegular style={{ color: COLORS.content.primary }}>
                  {formatTriggerCode(triggerCode)}
                </TitleRegular>
                <Button
                  buttonType="primary"
                  size="small"
                  buttonText="Copy"
                  onClick={handleCopyText}
                />
              </CodeDisplayContainer>
            </TestCard>
            
            <BodyCaption style={{ color: COLORS.content.secondary, textAlign: "center", marginTop: "8px" }}>
              {channel === CHANNELS.INSTAGRAM
                ? "Send this code to your Instagram account to trigger this journey"
                : channel === CHANNELS.EMAIL
                ? "Send this code to your Email to trigger this journey"
                : "Send this code to your Whatsapp account to trigger this journey"
              }
            </BodyCaption>
          </LiveTestModalContent>
        </StyledModal>
      )}
    </>
  );
};

// Styled Components
const PopoverContainer = styled.div`
  background-color: ${COLORS.surface.standard};
  border-radius: 4px;
  width: 380px;
  box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.12);
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const OptionCard = styled.div`
  border: 1px solid ${COLORS.stroke.primary};
  border-radius: 4px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.2s;
  background-color: ${COLORS.surface.standard};

  &:hover {
    background-color: ${COLORS.surface.hovered};
    border-color: ${COLORS.content.brand};
  }
`;

const PreviewModeCard = styled.div`
  border: 1px solid ${COLORS.stroke.primary};
  border-radius: 4px;
  padding: 12px;
  background-color: ${COLORS.surface.standard};
  display: flex;
  flex-direction: column;
`;

const PreviewModeCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const OptionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LiveTestModalContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const TestCard = styled.div`
  background-color: ${COLORS.surface.hovered};
  border-radius: 8px;
  padding: 20px;
  display: flex;
  flex-direction: column;
`;

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const QRCodeContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
  background-color: ${COLORS.surface.standard};
  border-radius: 8px;
  border: 1px solid ${COLORS.stroke.primary};
`;

const Divider = styled.div<{ insideCard?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: ${(props) => props.insideCard ? "20px 0" : "16px 0"};
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    height: 1px;
    background-color: ${COLORS.stroke.primary};
  }
`;

const DividerText = styled.span<{ insideCard?: boolean }>`
  background-color: ${(props) => props.insideCard ? COLORS.surface.hovered : COLORS.surface.standard};
  padding: 0 12px;
  color: ${COLORS.content.secondary};
  font-size: 14px;
  position: relative;
  z-index: 1;
`;

const LinkButton = styled.span`
  color: ${COLORS.content.brand};
  text-decoration: underline;
  cursor: pointer;
  
  &:hover {
    opacity: 0.8;
  }
`;

const CodeDisplayContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const PreviewWarningContainer = styled.div`
  margin-top: 8px;
  border: 1px solid ${COLORS.stroke.warning.lightAlt};
  background: ${COLORS.background.warning.light};
  border-radius: 4px;
  padding: 8px;
  display: flex;
  align-items: flex-start;
  gap: 8px;

  svg {
    flex-shrink: 0;
  }
  a {
    color: ${COLORS.content.brand} !important;
    text-decoration: underline !important;
    font-size: 14px;
  }
`;
